import ChatService from "src/components/pages/home/service/chatService";
import { SourceAttribution } from "src/components/pages/home/store/types";
import apiClient from "src/utils/apiClient";
import { v4 as uuidv4 } from "uuid";
import {
  asFileType,
  failureKey,
  KNOWN_FILE_TYPES,
  REGION_CODES,
} from "../adminReportUtils";
import {
  AdminReportOverview,
  ChangeType,
  DocumentTestCase,
  FailedCase,
  FileType,
  PolicyDocument,
  QuestionPreview,
  RegionCode,
  RegionReport,
  TestCaseUpdate,
} from "../types";
import {
  FailedCaseDTO,
  GetDocumentsDTO,
  GetFailuresDTO,
  GetOverviewDTO,
  PolicyDocumentDTO,
  RegionOverviewDTO,
} from "./types";

const secondsToDate = (seconds?: number | null) =>
  seconds ? new Date(seconds * 1000) : null;

const asNumber = (value?: number | null) =>
  value == null ? null : Number(value);

class AdminReportService {
  private readonly baseURL = "/admin/report";

  // data mapping
  private mapTestCase(dto: PolicyDocumentDTO): DocumentTestCase | null {
    if (!dto.test_case_id) return null;

    return {
      testCaseId: dto.test_case_id,
      question: dto.test_case_question ?? "",
      comment: dto.test_case_comment ?? null,
      createdAt: secondsToDate(dto.test_case_created_at),
      createdBy: dto.test_case_created_by ?? "",
    };
  }

  /**
   * Nearly every attribute is nullable on the wire, so each one is defaulted
   * here rather than guarded at every render site.
   */
  private mapDocument(
    dto: PolicyDocumentDTO,
    region: RegionCode,
  ): PolicyDocument {
    return {
      attachmentId: dto.attachment_id,
      region: (dto.region as RegionCode) ?? region,
      policyId: dto.node_id ?? "",
      nodeTitle: dto.node_title ?? "",
      nodeDescription: dto.node_description ?? "",
      nodeDocumentType: dto.node_document_type ?? "",
      nodeFullPath: dto.node_full_path ?? "",
      nodeInstanceType: dto.node_instance_type ?? "",
      nodeStatus: dto.node_status ?? "",
      fileName: dto.file_name ?? "",
      fileType: asFileType(dto.file_type),
      substringName: dto.substring_name ?? "",
      s3Uri: dto.s3_uri ?? "",
      sizeBytes: dto.size_bytes ?? 0,
      language: dto.language ?? "",
      isGlobal: dto.is_global === "yes",
      changeType: (dto.change_type as ChangeType) ?? "EXISTING",
      changedAt: secondsToDate(dto.changed_at),
      firstSeenAt: secondsToDate(dto.first_seen_at),
      lastModifiedAt: secondsToDate(dto.last_modified_at),
      lastSeenAt: secondsToDate(dto.last_seen_at),
      excludeReason: dto.exclude_reason ?? null,
      policyUrl: dto.policy_url ?? null,
      testCase: this.mapTestCase(dto),
    };
  }

  /**
   * file_counts is keyed by whatever file_type each row held, so it can carry
   * an "unknown" bucket or a type this UI has no column for. Everything outside
   * the four known types is folded into "other" so the counts still sum to
   * searchable_document_count and the charts cannot silently under-report.
   */
  private mapFileCounts(counts: Record<string, number> = {}) {
    const mapped = {} as Record<FileType, number>;
    let known = 0;

    KNOWN_FILE_TYPES.forEach((fileType) => {
      mapped[fileType] = counts[fileType] ?? 0;
      known += mapped[fileType];
    });

    const total = Object.keys(counts).reduce(
      (sum, key) => sum + (counts[key] ?? 0),
      0,
    );
    mapped.other = Math.max(0, total - known);

    return mapped;
  }

  private mapRegionReport(
    region: RegionCode,
    dto: RegionOverviewDTO,
  ): RegionReport {
    return {
      region,
      runId: dto.run_id ?? null,
      startedAt: secondsToDate(dto.started_at),
      finishedAt: secondsToDate(dto.finished_at),
      fileCounts: this.mapFileCounts(dto.file_counts),
      searchableDocumentCount: dto.searchable_document_count ?? 0,
      uncoveredDocumentCount: dto.uncovered_document_count ?? 0,
      testCaseCount: dto.test_case_count ?? 0,
      passed: asNumber(dto.passed),
      failed: asNumber(dto.failed),
      testCasesRecorded: asNumber(dto.test_cases_recorded),
      byReasonCode: dto.by_reason_code ?? {},
    };
  }

  private mapFailedCase(
    region: RegionCode,
    finishedAt: number | null | undefined,
    dto: FailedCaseDTO,
  ): FailedCase {
    return {
      region,
      attachmentId: dto.attachment_id,
      testCaseId: dto.test_case_id ?? "",
      reasonCode: dto.reason_code,
      question: dto.test_case_question ?? "",
      expectedFileName: dto.expected_file_name ?? "",
      returnedTitles: dto.returned_titles ?? [],
      lastRunAt: secondsToDate(finishedAt),
    };
  }

  // api service methods
  async getOverview(): Promise<AdminReportOverview> {
    const { data } = await apiClient.get<GetOverviewDTO>(
      `${this.baseURL}/overview`,
    );
    const present = Object.keys(data.by_region) as RegionCode[];

    return {
      regions: present.map((region) =>
        this.mapRegionReport(region, data.by_region[region]),
      ),
      // A realm the service could not read is omitted, not zeroed.
      unavailableRegions: REGION_CODES.filter(
        (region) => present.indexOf(region) === -1,
      ),
    };
  }

  async getDocuments(region: RegionCode): Promise<PolicyDocument[]> {
    const { data } = await apiClient.get<GetDocumentsDTO>(
      `${this.baseURL}/documents`,
      { params: { region } },
    );

    return data.documents.map((document) =>
      this.mapDocument(document, data.region ?? region),
    );
  }

  /**
   * One file's record. `region` is required: attachment_id is derived from the
   * file, so a global file carries the same id in all eight tables.
   */
  async getDocument(
    region: RegionCode,
    attachmentId: string,
  ): Promise<PolicyDocument | null> {
    const { data } = await apiClient.get<PolicyDocumentDTO>(
      `${this.baseURL}/documents/${encodeURIComponent(attachmentId)}`,
      { params: { region } },
    );

    return this.mapDocument(data, region);
  }

  /** Every region's failing cases from its latest run, flattened for the table. */
  async getFailures(): Promise<FailedCase[]> {
    const { data } = await apiClient.get<GetFailuresDTO>(
      `${this.baseURL}/failures`,
    );

    return (Object.keys(data.by_region) as RegionCode[]).flatMap((region) => {
      const run = data.by_region[region];
      return (run.failed_cases ?? []).map((failedCase) =>
        this.mapFailedCase(region, run.finished_at, failedCase),
      );
    });
  }

  /**
   * The current test case behind each failing row, keyed by failureKey, for the
   * rows the attachments table has moved on from.
   *
   * Failures come from the run history table and every write lands on the
   * attachments table, so a case that has already been fixed keeps failing on
   * screen until the next run. One lookup per failing row: failures are a small
   * subset of a region, so this is far less data than the region's whole
   * document list. Never rejects, since a row the service cannot read simply
   * stays as the run recorded it.
   */
  async getFailureUpdates(
    failedCases: FailedCase[],
  ): Promise<Record<string, TestCaseUpdate>> {
    const updates: Record<string, TestCaseUpdate> = {};

    await Promise.all(
      failedCases.map(async (failedCase) => {
        try {
          const document = await this.getDocument(
            failedCase.region,
            failedCase.attachmentId,
          );
          const update = this.testCaseUpdate(failedCase, document);

          if (update) updates[failureKey(failedCase)] = update;
        } catch {
          // No lookup, no claim about the row.
        }
      }),
    );

    return updates;
  }

  /**
   * Null when the attachment still holds the question that ran, so an untouched
   * row carries no marker. The timestamp alone is not enough: a re-save of the
   * same text moves it, and an edit the publisher wrote without one would not,
   * so the question text is compared too.
   */
  private testCaseUpdate(
    failedCase: FailedCase,
    document: PolicyDocument | null,
  ): TestCaseUpdate | null {
    if (!document) return null;

    const { testCase } = document;
    if (!testCase) {
      return { question: null, updatedAt: null, updatedBy: null };
    }

    const isUpdated =
      testCase.question !== failedCase.question ||
      (testCase.createdAt != null &&
        failedCase.lastRunAt != null &&
        testCase.createdAt > failedCase.lastRunAt);

    return isUpdated
      ? {
          question: testCase.question,
          updatedAt: testCase.createdAt,
          updatedBy: testCase.createdBy,
        }
      : null;
  }

  /** Adds or replaces the question on a file. Returns the updated record. */
  async saveTestCase(
    region: RegionCode,
    attachmentId: string,
    question: string,
  ): Promise<PolicyDocument> {
    const { data } = await apiClient.put<PolicyDocumentDTO>(
      `${this.baseURL}/documents/${encodeURIComponent(attachmentId)}/test-case`,
      { question },
      { params: { region } },
    );

    return this.mapDocument(data, region);
  }

  /** Clears the test case, which returns the file to the uncovered list. */
  async deleteTestCase(
    region: RegionCode,
    attachmentId: string,
  ): Promise<PolicyDocument> {
    const { data } = await apiClient.delete<PolicyDocumentDTO>(
      `${this.baseURL}/documents/${encodeURIComponent(attachmentId)}/test-case`,
      { params: { region } },
    );

    return this.mapDocument(data, region);
  }

  /**
   * Replays a candidate question through SAIGE chat so the admin can see
   * whether it actually retrieves the document before saving it.
   */
  async previewQuestion(
    question: string,
    expectedFileName: string,
  ): Promise<QuestionPreview> {
    const { data } = await ChatService.postChart({
      query: question,
      conversation_id: uuidv4(),
    });
    const sourceAttributions: SourceAttribution[] =
      data.source_attributions ?? [];

    return {
      llmMessage: data.llm_message ?? "",
      sourceAttributions,
      relatedDocuments: data.related_documents ?? [],
      expectedCited: this.isCited(sourceAttributions, expectedFileName),
    };
  }

  /**
   * Only an attribution carrying text segments was actually cited in the
   * answer. Q Business also returns documents it never cited, which is exactly
   * what the NOT_CITED failures are about.
   */
  private isCited(
    sourceAttributions: SourceAttribution[],
    expectedFileName: string,
  ) {
    const expected = expectedFileName.trim().toLowerCase();

    return sourceAttributions.some(
      (attribution) =>
        (attribution.textMessageSegments?.length ?? 0) > 0 &&
        attribution.title?.trim().toLowerCase() === expected,
    );
  }
}

export default new AdminReportService();
